import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NzBreadCrumbModule } from 'ng-zorro-antd/breadcrumb';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import {
  NzFormControlComponent,
  NzFormItemComponent,
  NzFormLabelComponent,
  NzFormModule,
} from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { OrgMode, OrgTitles } from '../organization.enums';
import {
  OrganizationContactDrawerParams,
  UserResultModel,
} from '../organization.interface';
import { finalize, Observable, of, Subject, take, takeUntil } from 'rxjs';
import { DrawerService } from '../../../core/services/drawer.service';
import { OrganizationService } from '../organization.service';
import { ConfirmDialogComponent } from '../../../components/confirm-dialog/confirm-dialog.component';
import { DRAWER_IDS } from '../../../core/constants/drawer-ids.constant';
import { ErrorResponse } from '../../../core/models/error.models';
import { CommonService } from '../../../shared/services/common.service';
import { LoaderService } from '../../../core/services/loader.service';
import { AuditContextService } from '../../audit-logs/audit-context.service';

@Component({
  selector: 'app-org-contact-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NzFormControlComponent,
    NzFormItemComponent,
    NzFormLabelComponent,
    NzSelectModule,
    NzFormModule,
    NzCheckboxModule,
    NzBreadCrumbModule,
    NzButtonModule,
    ConfirmDialogComponent,
    NzSpinModule,
    CommonModule,
    NzInputModule,
  ],
  templateUrl: './org-contact-form.component.html',
  styleUrl: './org-contact-form.component.scss',
})
export class OrgContactFormComponent implements OnInit, OnDestroy {
  readonly OrganizationMode = OrgMode;

  loading$: Observable<boolean> = of(false);

  organizationContactForm!: FormGroup;
  orgParams: OrganizationContactDrawerParams = this.getDefaultParams();
  organizationContactData: UserResultModel = {} as UserResultModel;

  namePattern = /^[a-zA-Z][a-zA-Z\s]*[a-zA-Z]$/;

  isVisible = false;
  private readonly destroy$ = new Subject<void>();
  private readonly formControls = {
    firstName: [
      '',
      [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(50),
        Validators.pattern(this.namePattern),
      ],
    ],
    lastName: [
      '',
      [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(50),
        Validators.pattern(this.namePattern),
      ],
    ],
    title: [
      '',
      [
        Validators.required,
        Validators.maxLength(100),
        Validators.pattern(this.namePattern),
      ],
    ],
    email: [
      '',
      [
        Validators.required,
        Validators.email,
        Validators.pattern(
          String.raw`^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$`
        ),
      ],
    ],
    primaryPhone: [
      '',
      [
        Validators.required,
        Validators.pattern(
          String.raw`^[+]?[(]?[0-9]{3}[)]?[-s.]?[0-9]{3}[-s.]?[0-9]{4}$`
        ),
      ],
    ],
    secondaryPhone: [
      '',
      [
        Validators.pattern(
          String.raw`^[+]?[(]?[0-9]{3}[)]?[-s.]?[0-9]{3}[-s.]?[0-9]{4}$`
        ),
      ],
    ],
  };

  constructor(
    private readonly fb: FormBuilder,
    private readonly drawerService: DrawerService,
    private readonly organizationService: OrganizationService,
    private readonly commonService: CommonService,
    private readonly loaderService: LoaderService,
    private readonly auditContextService: AuditContextService
  ) {
    this.organizationContactForm = this.initForm();
    this.loading$ = this.loaderService.loading$;
  }

  ngOnInit(): void {
    this.setupDrawerSubscription();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isViewMode(): boolean {
    return this.orgParams.mode === OrgMode.VIEW_CONTACT;
  }

  get isEditMode(): boolean {
    return this.orgParams.mode === OrgMode.EDIT_CONTACT;
  }

  get isAddMode(): boolean {
    return this.orgParams.mode === OrgMode.ADD_CONTACT;
  }

  onEditContactFormOrganization(): void {
    this.orgParams.mode = OrgMode.EDIT_CONTACT;
    this.drawerService.updateDrawerTitle(
      DRAWER_IDS.CONTACT,
      OrgTitles.EDIT_CONTACT
    );
    this.organizationContactForm.enable();
  }

  onSubmit(event: Event): void {
    event.preventDefault();
    if (this.organizationContactForm.invalid) return;

    this.loaderService.show();
    const payload = this.createPayload();
    const _modifiedFields = this.buildContactModifiedFields(payload);
    const apiPayload = { ...payload, _modifiedFields };

    const operation$ =
      this.isEditMode && this.organizationContactData.id
        ? this.organizationService.updateContact(
            this.organizationContactData.id,
            apiPayload as UserResultModel
          )
        : this.organizationService.createContact(apiPayload as UserResultModel);

    operation$
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          this.loaderService.hide();
        })
      )
      .subscribe({
        next: () => this.handleSubmissionSuccess(),
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
        },
      });
  }

  private initForm(): FormGroup {
    return this.fb.group(this.formControls);
  }

  private getDefaultParams(): OrganizationContactDrawerParams {
    return {
      organizationId: '',
      mode: OrgMode.ADD_CONTACT,
      orgContact: undefined,
    };
  }

  private setupDrawerSubscription(): void {
    this.drawerService
      .getParams<OrganizationContactDrawerParams>(DRAWER_IDS.CONTACT)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.orgParams = params;
          if (this.orgParams.orgContact) {
            this.organizationContactData = this.orgParams.orgContact;
            this.patchFormValues(this.orgParams.orgContact);
            this.organizationContactForm.disable();
          }
        }
      });
  }

  private handleSubmissionSuccess(): void {
    const action = this.isEditMode ? 'updated' : 'created';
    this.showNotification(action);
    this.drawerService.closeAllDrawers();
  }

  private patchFormValues(org: UserResultModel): void {
    this.organizationContactForm.patchValue({
      organizationId: this.orgParams.organizationId,
      ...org,
    });
  }

  private createPayload(): UserResultModel {
    return {
      organizationId: Number(this.orgParams.organizationId),
      ...this.organizationContactForm.value,
    };
  }

  private buildContactModifiedFields(
    payload: Partial<UserResultModel>
  ): unknown[] {
    const labelMap = this.getContactAuditLabelMap();
    const { original, updated } = this.getContactAuditContext(payload);
    const excludedFields = ['id', 'organizationId'];
    return this.auditContextService.buildFieldChanges(original, updated, {
      sensitiveFields: [],
      labelMap,
      excludeFields: excludedFields,
    });
  }

  private getContactAuditLabelMap(): Record<string, string> {
    return {
      firstName: 'First Name',
      lastName: 'Last Name',
      title: 'Title',
      email: 'Email',
      primaryPhone: 'Primary Phone',
      secondaryPhone: 'Secondary Phone',
      organizationId: 'Organization',
    };
  }

  private getContactAuditContext(payload: Partial<UserResultModel>): {
    original: Partial<UserResultModel>;
    updated: Partial<UserResultModel>;
  } {
    if (!this.isEditMode) {
      return {
        original: {},
        updated: payload,
      };
    }

    return {
      original: { ...this.organizationContactData },
      updated: { ...payload },
    };
  }

  closeContactDrawer(): void {
    this.drawerService.closeDrawer(DRAWER_IDS.CONTACT);
  }

  showDeleteModal(): void {
    this.isVisible = true;
  }
  handleOkMiddle(): void {
    this.isVisible = false;
    this.deleteContactFormOrganization();
  }
  handleCancelMiddle(): void {
    this.isVisible = false;
  }

  deleteContactFormOrganization(): void {
    const id = Number(this.organizationContactData.id);
    this.loaderService.show();
    this.organizationService
      .deleteContact(id)
      .pipe(
        finalize(() => {
          this.loaderService.hide();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          this.showNotification('deleted');
          this.drawerService.closeAllDrawers();
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
        },
      });
  }

  private showNotification(action: string): void {
    const message = `Organization contact ${action} successfully`;
    this.commonService.notify('success', message, '', '450px');
  }
}
